/**
 * Utilities for opening external system links in properly sized popup windows.
 * Provides consistent behavior across the application for Lightspeed, Xero, and other external systems.
 */

export interface PopupConfig {
  maxWidth?: number;
  maxHeight?: number;
  widthPercent?: number;
  heightPercent?: number;
  windowName?: string;
}

/**
 * Opens a URL in a properly sized popup window with fallback handling.
 * Each call opens a new window (never reuses existing windows).
 * 
 * @param url - The URL to open
 * @param itemName - Name of the item being opened (for user-friendly messages)
 * @param config - Popup window configuration
 */
export const openExternalPopup = (
  url: string, 
  itemName: string, 
  config: PopupConfig = {}
) => {
  try {
    const {
      maxWidth = 1180,
      maxHeight = window.innerHeight * 0.8,
      widthPercent = 0.8,
      heightPercent = 0.8,
      windowName
    } = config;

    // Generate unique window name to ensure each click opens a new window
    const uniqueWindowName = windowName ? `${windowName}-${Date.now()}` : `popup-${Date.now()}`;

    const width = Math.min(window.innerWidth * widthPercent, maxWidth);
    const height = Math.min(window.innerHeight * heightPercent, maxHeight);
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    const popup = window.open(
      url,
      uniqueWindowName,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    );
    
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      // Popup was blocked - show fallback.
      // intentionally native: this is a synchronous utility (not a React
      // component), so it can't await a ConfirmDialog inline.
      if (window.confirm(`Pop-up blocked. Would you like to open ${itemName} in a new tab instead?`)) {
        window.open(url, '_blank');
      }
    }
  } catch (error) {
    console.error('Failed to open external popup window:', error);
    // Fallback to new tab
    window.open(url, '_blank');
  }
};

/**
 * Handle click events for external links with proper modifier key support.
 * 
 * @param event - Mouse click event
 * @param url - The URL to open
 * @param itemName - Name of the item being opened
 * @param config - Popup window configuration
 */
export const handleExternalLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  url: string,
  itemName: string,
  config: PopupConfig = {}
) => {
  // Only prevent default for left clicks (button 0) without modifier keys
  // Right clicks (button 2), middle clicks (button 1), Ctrl+click, etc. should work normally
  if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
    event.preventDefault();
    openExternalPopup(url, itemName, config);
  }
  // For right-click, Ctrl+click, Cmd+click, etc., let the browser handle it normally
};

/**
 * Predefined configurations for different external systems
 */
export const POPUP_CONFIGS = {
  lightspeed: {
    maxWidth: 1180,
    widthPercent: 0.8,
    heightPercent: 0.8,
    windowName: 'lightspeed-popup'
  },
  xero: {
    maxWidth: 1200,
    widthPercent: 0.85,
    heightPercent: 0.85,
    windowName: 'xero-popup'
  },
  default: {
    maxWidth: 1180,
    widthPercent: 0.8,
    heightPercent: 0.8,
    windowName: 'external-popup'
  }
} as const;

/**
 * Convenience function for opening Lightspeed links
 */
export const openLightspeedLink = (url: string, itemName: string) => {
  openExternalPopup(url, itemName, POPUP_CONFIGS.lightspeed);
};

/**
 * Convenience function for opening Xero links
 */
export const openXeroLink = (url: string, itemName: string) => {
  openExternalPopup(url, itemName, POPUP_CONFIGS.xero);
};

/**
 * Convenience function for handling Lightspeed link clicks
 */
export const handleLightspeedLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  url: string,
  itemName: string
) => {
  handleExternalLinkClick(event, url, itemName, POPUP_CONFIGS.lightspeed);
};

/**
 * Convenience function for handling Xero link clicks
 */
export const handleXeroLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  url: string,
  itemName: string
) => {
  handleExternalLinkClick(event, url, itemName, POPUP_CONFIGS.xero);
};